import {ResourceType} from '../../../../@types/resource-type';
import {ExtendedTreeViewItem} from './explorer-mrt-types';
import authService from '../../../../services/auth.service';
import roleToRights, {groupRights} from '../../../../utils/role-to-rights';

export type ActionType =
    | 'edit'
    | 'duplicate'
    | 'move'
    | 'delete'
    | 'archive'
    | 'restore'
    | 'hardDelete'
    | 'newComponent'
    | 'newEventSequence'
    | 'importEventSequence'
    | 'exportEventSequence'
    | 'view'
    | 'newSubGroup'
    | 'leaveGroup'
    | 'properties'
    | 'mapping'
    | 'deactivate'
    | 'reissueApiKey'
    | 'publish'
    | 'sensitivityAnalysis'
    | 'uncertaintyAnalysis'
    | 'run'
    | 'versionHistory'
    | 'export'
    | 'import'
    | 'importMapping'
    | 'syncChanged'
    | 'syncFull'
    | 'syncOpen'
    | 'rebuild'
    | 'reindex'
    | 'taxonomyTag'
    | 'taxonomyRetag';

export interface ActionConfig {
    type: ActionType;
    label: string;
    tooltip?: string; // Tooltip text to explain the action
    divider?: boolean; // Add divider before this action
    isDisabled?: (item: ExtendedTreeViewItem) => boolean; // Function to determine if action is disabled
    getLabel?: (item: ExtendedTreeViewItem) => string; // Function to get dynamic label
}

export const resourceActionConfigs: Partial<Record<ResourceType, ActionConfig[]>> = {
    [ResourceType.SAFETY_MODELS]: [
        { type: 'edit', label: 'Edit' },
        { type: 'duplicate', label: 'Duplicate' },
        { type: 'move', label: 'Move' },
        { type: 'delete', label: 'Delete' },
        { type: 'newEventSequence', label: 'New Safety View', divider: true },
        { type: 'importEventSequence', label: 'Import Safety View' },
        { type: 'exportEventSequence', label: 'Export Safety View' },
    ],
    
    [ResourceType.OCCURRENCE_REPORTS]: [
        { type: 'view', label: 'View' },
    ],

    // Syncing a source rewrites the local copy everyone reads, so it is a super user's to run.
    [ResourceType.SAFETY_RECOMMENDATIONS]: [
        {
            type: 'syncChanged',
            label: 'Sync (changed)',
            tooltip: 'Fetch what the source has added or changed since the last sync (super users only)',
            isDisabled: (item): boolean => !authService.isSuperUser() || Boolean(item.object?.jobRunning),
        },
        {
            type: 'syncOpen',
            label: 'Sync (open)',
            tooltip: 'Refetch every recommendation still open, picking up new letters (super users only)',
            isDisabled: (item): boolean => !authService.isSuperUser() || Boolean(item.object?.jobRunning),
        },
        {
            type: 'syncFull',
            label: 'Sync (everything)',
            tooltip: 'Refetch every recommendation the source has (super users only)',
            isDisabled: (item): boolean => !authService.isSuperUser() || Boolean(item.object?.jobRunning),
        },
        {
            type: 'reindex',
            label: 'Update search index',
            divider: true,
            tooltip: 'Embed every recommendation and linked report whose text has changed or that was embedded by '
                + 'a different model; current ones are skipped. Run after a model change or an outage (super users only)',
            isDisabled: (item): boolean => !authService.isSuperUser() || Boolean(item.object?.jobRunning),
        },
        {
            type: 'taxonomyTag',
            label: 'Tag with taxonomy',
            tooltip: 'Classify recommendations against the recommendation taxonomy; syncs already tag as they '
                + 'go (super users only)',
            isDisabled: (item): boolean => !authService.isSuperUser() || Boolean(item.object?.jobRunning),
        },
        {
            type: 'taxonomyRetag',
            label: 'Tag with taxonomy (everything again)',
            tooltip: 'Classify every recommendation again, replacing our own tags and leaving the source\'s '
                + 'coding alone; a full run over the corpus (super users only)',
            isDisabled: (item): boolean => !authService.isSuperUser() || Boolean(item.object?.jobRunning),
        },
        {
            type: 'rebuild',
            label: 'Rebuild from held records',
            tooltip: 'Re-translate everything held without calling the source (super users only)',
            isDisabled: (item): boolean => !authService.isSuperUser() || Boolean(item.object?.jobRunning),
        },
    ],
    
    [ResourceType.OCCURRENCE_REPORT_DESCRIPTORS]: [
        { type: 'properties', label: 'Properties' },
        { type: 'export', label: 'Export' },
        {
            type: 'importMapping',
            label: 'Import Mapping',
            isDisabled: (item): boolean => {
                const descriptor = item.object;
                return descriptor?.descriptorState !== 'PUBLISHED';
            }
        },
        {
            type: 'delete',
            label: 'Delete',
            divider: true,
            isDisabled: (item): boolean => {
                const descriptor = item.object;
                if (descriptor?.descriptorState === 'PUBLISHED') {
                    return !authService.isSuperUser();
                }
                return false;
            }
        },
    ],
    
    [ResourceType.GROUPS]: [
        { 
            type: 'edit', 
            label: 'Members',
            getLabel: (): string => authService.isSuperUser() ? 'Edit' : 'Members',
            isDisabled: (item): boolean => {
                // Check if group has canEdit method
                const group = item.object;
                if (group && typeof group.canEdit === 'function') {
                    return !group.canEdit();
                }
                // Fallback: check role
                const role = group?.currentUser?.role;
                return !(role === 'ADMIN' || authService.isSuperUser());
            }
        },
        { 
            type: 'newSubGroup', 
            label: 'New Sub Working Area',
            isDisabled: (item): boolean => {
                const group = item.object;
                if (group && typeof group.canEdit === 'function') {
                    return !group.canEdit();
                }
                const role = group?.currentUser?.role;
                return !(role === 'ADMIN' || authService.isSuperUser());
            }
        },
        {
            // A Component belongs to a working area rather than to a safety model. Creating one
            // here preselects this working area in the form; the dataset's Create button also
            // offers it, without a working area chosen.
            type: 'newComponent',
            label: 'New Component',
            isDisabled: (item): boolean => {
                if (authService.isSuperUser()) {
                    return false;
                }
                // groupRole is the effective role the tree already derived for this row, whichever
                // field carried it; the row's object is a raw group DTO and has no rights methods.
                return !roleToRights(item.groupRole, 'group').includes(groupRights.addOrEditResources);
            }
        },
        {
            type: 'duplicate',
            label: 'Duplicate',
            isDisabled: (item): boolean => {
                const group = item.object;
                // First check canEdit permission
                const canEdit = group && typeof group.canEdit === 'function' ? group.canEdit() :
                    (group?.currentUser?.role === 'ADMIN' || authService.isSuperUser());

                if (!canEdit) return true;

                // Non-super users cannot duplicate top-level groups
                return !authService.isSuperUser() && !group?.parent;


            }
        },
        {
            type: 'move',
            label: 'Move',
            isDisabled: (item): boolean => {
                const group = item.object;
                const canEdit = group && typeof group.canEdit === 'function' ? group.canEdit() :
                    (group?.currentUser?.role === 'ADMIN' || authService.isSuperUser());
                return !canEdit;
            }
        },
        {
            type: 'leaveGroup',
            label: 'Leave Working Area',
            divider: true,
            isDisabled: (item): boolean => {
                const group = item.object;
                if (group && typeof group.canLeave === 'function') {
                    return !group.canLeave();
                }
                // Can leave if user is a member
                return !group?.currentUser;
            }
        },
        { 
            type: 'delete', 
            label: 'Delete',
            isDisabled: (item): boolean => {
                const group = item.object;
                // First check canEdit permission
                const canEdit = group && typeof group.canEdit === 'function' ? group.canEdit() : 
                    (group?.currentUser?.role === 'ADMIN' || authService.isSuperUser());
                
                if (!canEdit) return true;
                
                // Non-super users cannot delete top-level groups
                return !authService.isSuperUser() && !group?.parent;
                

            }
        },
    ],
    
    [ResourceType.PERFORMANCE_SHAPING_FACTORS]: [
        { type: 'properties', label: 'Properties' },
        { type: 'duplicate', label: 'Duplicate' },
        { type: 'delete', label: 'Delete' },
    ],
    
    [ResourceType.USERS]: [
        {
            type: 'edit',
            label: 'Edit',
            /* eslint-disable-next-line @typescript-eslint/no-unused-vars */
            isDisabled: (item): boolean => {
                return localStorage.getItem('eimMode') == 'true';
            }
        },
        {
            type: 'deactivate',
            label: 'Deactivate',
            isDisabled: (item): boolean => {
                const user = item.object;
                return user && user.superUser;
            }
        },
        {
            type: 'delete',
            label: 'Delete',
            isDisabled: (item): boolean => {
                const user = item.object;
                return user && user.superUser;
            }
        },
    ],
    
    [ResourceType.SERVICE_USER_ACCOUNTS]: [
        { type: 'reissueApiKey', label: 'Reissue API Key' },
        { type: 'delete', label: 'Delete' },
    ],

    [ResourceType.FOUNDRY_EVENTS]: [],
    
    // Event sequences match the original explorer menu exactly
    [ResourceType.EVENT_SEQUENCES]: [
        { type: 'properties', label: 'Properties' },
        { type: 'edit', label: 'Edit' },
        { type: 'duplicate', label: 'Duplicate' },
        { type: 'move', label: 'Move' },
        { type: 'sensitivityAnalysis', label: 'Sensitivity Analysis', divider: true },
        { type: 'uncertaintyAnalysis', label: 'Uncertainty Analysis' },
        { type: 'versionHistory', label: 'Version History', divider: true },
        { type: 'exportEventSequence', label: 'Export', divider: true },
        { type: 'delete', label: 'Delete', divider: true },
    ],
    
    [ResourceType.COMPONENT]: [
        { type: 'properties', label: 'Properties' },
        {
            type: 'edit',
            label: 'Edit',
            // A published release is frozen; opening it is a look, not an edit. The editor
            // already renders read-only for anything but a DRAFT, so only the label changes.
            getLabel: (item): string => item.object?.state === 'DRAFT' ? 'Edit' : 'View',
        },
        // Mapping a version against whole report sources is a view of its own, not part of the
        // canvas a click opens, so it needs its own way in.
        { type: 'mapping', label: 'Mapping' },
        // Only a draft is edited, so only a draft has history; a published release is frozen.
        {
            type: 'versionHistory',
            label: 'History',
            tooltip: 'Only DRAFT components have history',
            isDisabled: (item): boolean => item.object?.state !== 'DRAFT',
        },
        // No 'duplicate' until Workspace's DuplicateResource flow implements a COMPONENT case;
        // offering it dead-ends in a "not implemented" toast.
        {
            type: 'publish',
            label: 'Publish',
            tooltip: 'Only DRAFT components can be published',
            isDisabled: (item): boolean => item.object?.state !== 'DRAFT',
        },
        {
            type: 'delete',
            label: 'Delete',
            tooltip: 'Only DRAFT components can be deleted',
            isDisabled: (item): boolean => item.object?.state !== 'DRAFT',
        },
    ],

    // Active (non-archived) IRPs - full menu
    [ResourceType.INTEGRATED_RISK_PICTURES]: [
        {
            type: 'properties',
            label: 'View Properties',
        },
        {
            type: 'edit',
            label: 'Edit',
            tooltip: 'Edit IRP configuration (only available for DRAFT status)',
            isDisabled: (item): boolean => {
                const irp = item.object;
                const group = irp?.group;
                const userRole = group?.currentUser?.role;
                const canEdit = userRole === 'ADMIN' || userRole === 'ANALYST' || authService.isSuperUser();

                if (!canEdit) return true;
                return irp?.status !== 'DRAFT';
            }
        },
        {
            type: 'duplicate',
            label: 'Duplicate',
            tooltip: 'Duplicate IRP configuration',
        },
        {
            type: 'run',
            label: 'Run',
            tooltip: 'Submit IRP for processing',
            isDisabled: (item): boolean => {
                const irp = item.object;
                const group = irp?.group;
                const userRole = group?.currentUser?.role;
                const canRun = userRole === 'ADMIN' || userRole === 'ANALYST' || authService.isSuperUser();

                if (!canRun) return true;
                return irp?.status !== 'DRAFT' || irp?.estimatedReportCount == 0;
            }
        },
        {
            type: 'delete',
            label: 'Delete',
            getLabel: (item): string => {
                const irp = item.object;
                if (irp?.status === 'COMPLETED') {
                    return authService.isSuperUser() ? 'Delete' : 'Archive';
                }
                return 'Delete';
            },
            tooltip: 'Archive or delete IRP',
            divider: true,
            isDisabled: (item): boolean => {
                const irp = item.object;
                const group = irp?.group;
                const userRole = group?.currentUser?.role;
                const canDelete = userRole === 'ADMIN' || authService.isSuperUser();

                return !canDelete;
            }
        },
        {
            type: 'exportEventSequence',
            label: 'Export Results',
            tooltip: 'Export IRP results (only available for COMPLETED status)',
            isDisabled: (item): boolean => {
                const irp = item.object;
                return irp?.status !== 'COMPLETED';
            }
        },
    ],
};

// Archived IRPs - ONLY restore and hard delete
const archivedIRPActions: ActionConfig[] = [
    {
        type: 'restore',
        label: 'Restore',
        tooltip: 'Restore archived IRP to COMPLETED status (super users only)',
        isDisabled: (): boolean => {
            return !authService.isSuperUser();
        }
    },
    {
        type: 'hardDelete',
        label: 'Hard Delete',
        tooltip: 'Permanently delete archived IRP (super users only, cannot be undone)',
        isDisabled: (): boolean => {
            return !authService.isSuperUser();
        }
    },
];

// Get actions for a specific resource type
export const getResourceActions = (resourceType: ResourceType, isShowingArchived?: boolean): ActionConfig[] => {
    // Special handling for IRPs - return different actions based on whether we're showing archived items
    if (resourceType === ResourceType.INTEGRATED_RISK_PICTURES && isShowingArchived === true) {
        return archivedIRPActions;
    }

    // Return standard actions for the resource type
    return resourceActionConfigs[resourceType] || [];
};